import { getModel } from '../../../model/registry'
import React from "react"
import { conditionFields, evaluateCondition } from "../../../../../common/model/condition/evaluate"
import CardView from "../../cardView/CardView"
import CardEmbed from "../../cardView/CardEmbed"
import Tabs from "../../tabs/Tabs"
import styled from "styled-components"
import { useStore } from "../Page"
import SubList from "../../subList/SubList"
import ListView from "../../listView/ListView"
import GridList from "../../gridList/GridList"
import CardViewTabLines from "../../cardView/CardViewTabLines"
import DocumentDisplay from "../../generic/DocumentDisplay/DocumentDisplay"

const Wrapper = styled.div`
  max-width: ${ ({width}) => width };
`

const TabView = ({ type, options, data }) => {
  const config = getModel('tabViewData')[type]
  // `tabConditions` is a map keyed by tab, not a conjunction — each entry
  // decides one tab. The old array form read like an AND and was not one.
  const tabConditions = config.tabConditions ?? {}
  const tabNames = Object.keys(tabConditions)

  const [storeValues] = useStore((s) => {
    const result = {}
    for (const name of tabNames) {
      for (const f of conditionFields(tabConditions[name])) result[f] = s[f]
    }
    return result
  })

  const [storeData] = useStore(s => s)

  const conditionValue = tabNames.length > 0
    ? tabNames.map(tab => ({
        result: evaluateCondition(tabConditions[tab], { values: storeValues }),
        tab,
      }))
    : null

  // Resolve tabs into React elements based on declarative content
  const resolvedTabs = (config.tabs || []).map(tab => {
    if (typeof tab.content === 'string') {
      // Handle SubList via existing pattern
      if (tab.content === 'SubList') {
        return {
          ...tab,
          content: React.createElement(SubList, {
            data,
            useStore,
            tab
          })
        }
      }
      // Handle ListView via type/options from config
      if (tab.content === 'ListView') {
        return {
          ...tab,
          content: React.createElement(ListView, {
            type: tab.view ?? tab.listType,
            options: tab.options,
            embedded: true
          })
        }
      }
      // Handle GridList via gridListType from config
      if (tab.content === 'GridList') {
        return {
          ...tab,
          content: React.createElement(GridList, {
            type: tab.view ?? tab.gridListType,
            options: tab.options,
            useStore,
          })
        }
      }
      // Handle CardViewLines — inline array editor rendered in a tab
      if (tab.content === 'CardViewLines') {
        return {
          ...tab,
          content: React.createElement(CardViewTabLines, {
            tab,
            useStore,
            options,
            entity: config.view ?? config.cardViewType,
          })
        }
      }
      // Handle CardView — single-record form embedded in a tab
      if (tab.content === 'CardView') {
        return {
          ...tab,
          content: React.createElement(CardView, {
            type: tab.view ?? tab.cardType,
            options: tab.options || {},
          })
        }
      }
      // Handle CardEmbed — fetches child record, determines new/edit mode automatically
      if (tab.content === 'CardEmbed') {
        return {
          ...tab,
          content: React.createElement(CardEmbed, {
            type: tab.view ?? tab.cardType,
          })
        }
      }
      // Handle DocumentDisplay — document viewer for instruction tabs
      if (tab.content === 'DocumentDisplay') {
        return {
          ...tab,
          content: React.createElement(DocumentDisplay, {
            data: data || storeData,
            entity: config.view ?? config.cardViewType,
          })
        }
      }
    }
    return tab
  })

  return (
    <Wrapper width={config.width}>
      <CardView data={data} type={config.view ?? config.cardViewType} options={options} />
      <Tabs 
        config={resolvedTabs} 
        id={type} 
        type={''} 
        conditionValue={conditionValue}
      />
    </Wrapper>
  )
}

export default TabView
